import {
  BAD_GATEWAY_STATUS_CODE,
  GATEWAY_TIMEOUT_STATUS_CODE,
  INTERNAL_SERVER_ERROR_STATUS_CODE,
  REQUEST_TIMEOUT_STATUS_CODE,
  SERVICE_UNAVAILABLE_STATUS_CODE,
  TOO_MANY_REQUESTS_STATUS_CODE,
} from './http';

export interface ExponentialBackoffOptions {
  initialDelayMs?: number;
  maxDelayMs?: number;
  factor?: number;
  maxRetries?: number;
}

export const RETRYABLE_ERROR_MESSAGE = '![reserved] retryable error';
export const DEFAULT_MAX_DELAY_MS = 15000;
export const DEFAULT_MAX_RETRIES = 5;
export const DEFAULT_INITIAL_DELAY_MS = 5000;
export const DEFAULT_FACTOR = 2;

export const isErrorCodeRetryable = (code: number) => {
  return [
    BAD_GATEWAY_STATUS_CODE,
    GATEWAY_TIMEOUT_STATUS_CODE,
    INTERNAL_SERVER_ERROR_STATUS_CODE,
    REQUEST_TIMEOUT_STATUS_CODE,
    SERVICE_UNAVAILABLE_STATUS_CODE,
    TOO_MANY_REQUESTS_STATUS_CODE,
  ].includes(code);
};

export const isErrorDetailRetryable = (detail: string) => {
  // Detail is in format "[<error_code>] <error_message>"
  const errorCode = parseInt(detail.split(']')[0].replace('[', ''));
  return isErrorCodeRetryable(errorCode);
};

export const isErrorCode = (code: number) => {
  return code >= 400;
};

export const calculateExponentialBackoffDelay = (
  attempt: number,
  factor?: number,
  initialDelay?: number,
) => {
  return (
    (initialDelay ?? DEFAULT_INITIAL_DELAY_MS) *
    (factor ?? DEFAULT_FACTOR) ** (attempt - 1)
  );
};

export const ExponentialBackoff = async (
  fn: () => Promise<any>,
  retryOn?: (error: any) => boolean,
  options?: ExponentialBackoffOptions,
) => {
  if (!retryOn) {
    retryOn = (error) => error.message === RETRYABLE_ERROR_MESSAGE;
  }
  const initialDelay = options?.initialDelayMs || DEFAULT_INITIAL_DELAY_MS;
  const maxDelay = options?.maxDelayMs || DEFAULT_MAX_DELAY_MS;
  const factor = options?.factor || DEFAULT_FACTOR;
  const maxRetries = options?.maxRetries || DEFAULT_MAX_RETRIES;
  const errors = [];
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      errors.push(error);
      if (!retryOn(error)) {
        break;
      }
      const delay = Math.min(
        calculateExponentialBackoffDelay(attempt, factor, initialDelay),
        maxDelay,
      );
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw errors[errors.length - 1];
};
